import { Dialog, DialogCloseReason, DialogProps, DialogWrapperProps, HeaderMenuItem } from '@ytl/common-web';
import { createContext, useContext, useMemo, useState } from 'react';
import { AppRoutes } from '../routes';
import { useLanguage } from './languageContext';

export type AppContextProps =
  | undefined
  | {
      isLoading: boolean;
      setLoading: (value: boolean) => void;
      menuItems: Array<HeaderMenuItem>;
      setMenuItems: (value: Array<HeaderMenuItem>) => void;
      openDialog: (props: DialogProps) => Promise<DialogCloseReason>;
    };

export const AppContext = createContext<AppContextProps>(undefined);

export const AppProvider = ({ children }: { children: React.ReactNode }) => {
  const { translation } = useLanguage();
  const [dialogConfig, setDialogConfig] = useState<DialogWrapperProps | undefined>();

  const openDialog = (props: DialogProps) => {
    return new Promise<DialogCloseReason>(resolve => {
      setDialogConfig({
        ...props,
        onClose: (reason: DialogCloseReason) => {
          resolve(reason);
          setDialogConfig(undefined);
        },
      });
    });
  };

  const defaultMenuItems = useMemo(() => {
    return [
      {
        label: translation.header.menu.backToYettel,
        onClick() {
          AppRoutes.goToYettel();
        },
      },
    ];
  }, [translation]);

  const [isLoading, setLoading] = useState<boolean>(false);
  const [menuItems, setMenuItems] = useState<Array<HeaderMenuItem>>(defaultMenuItems);

  return (
    <AppContext.Provider
      value={{
        isLoading,
        setLoading,
        menuItems,
        setMenuItems,
        openDialog,
      }}
    >
      {children}
      {dialogConfig && <Dialog {...dialogConfig} />}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error(`useApp must be used within an AppProvider`);
  }
  return context;
};
